import './styles/App.css';
import { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './components/AuthProvider';
import { supabase } from './supabaseClient';

import Header from './components/Header';
import CV from './components/CV';
import Showreel from './components/Showreel';
import Project from './components/Project';
import AdminCMS from './components/AdminCMS';
import TwoColumnLayout from './components/TwoColumnLayout';

function AppContent() {
  const [cvVisible, setCVVisible] = useState(false);
  const [homepageMedia, setHomepageMedia] = useState([]);
  const [homepageItems, setHomepageItems] = useState([]);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [bannerConfig, setBannerConfig] = useState(null);
  const isWelcomePopupEnabled = true;
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    if (!isWelcomePopupEnabled || !bannerConfig?.enabled) {
      setShowWelcomePopup(false);
      return;
    }
    if (isHome) {
      setShowWelcomePopup(true);
    } else {
      setShowWelcomePopup(false);
    }
  }, [isHome, isWelcomePopupEnabled, bannerConfig]);

  useEffect(() => {
    document.body.style.overflow = showWelcomePopup ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showWelcomePopup]);

  useEffect(() => {
    async function fetchHomepageImage() {
      try {
        // Fetch homepage images from database
        const { data: homeImages, error } = await supabase
          .from('homepage_images')
          .select('*');
      
        if (error) throw error;
      
        if (!homeImages || homeImages.length === 0) {
          console.warn('No homepage images found in database');
          setHomepageMedia([]);
          return;
        }
      
        // Check if we've shown the primary image this session
        const hasSeenPrimary = sessionStorage.getItem('hasSeenPrimaryImage');
        
        let selectedImage;
        
        if (!hasSeenPrimary) {
          // First visit this session - show primary image
          const primaryImage = homeImages.find(img => img.is_primary === true);
          selectedImage = primaryImage || homeImages[0]; // fallback to first if no primary
          
          // Mark that we've shown the primary image
          sessionStorage.setItem('hasSeenPrimaryImage', 'true');
        } else {
          // Subsequent visits - show random image
          selectedImage = homeImages[Math.floor(Math.random() * homeImages.length)];
        }
      
        console.debug('App: fetched homepage images count=', homeImages.length);
        console.debug('App: selected image=', selectedImage.url, 'isPrimary:', !hasSeenPrimary);
      
        setHomepageMedia([{
          type: 'image',
          src: selectedImage.url,
          name: selectedImage.name || 'homepage image',
          size: 'full',
          orientation: 'landscape',
          link_project_id: selectedImage.link_project_id || null,
          link_url: selectedImage.link_url || null,
        }]);
      } catch (err) {
        console.error('Error fetching homepage images:', err);
        setHomepageMedia([]);
      }
    }

    fetchHomepageImage();
  }, []);

  useEffect(() => {
    async function fetchHomepageConfig() {
      try {
        const { data, error } = await supabase
          .from('texts')
          .select('text')
          .eq('name', 'homepage_config')
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        if (!data?.text) {
          setHomepageItems([]);
          return;
        }

        const parsed = JSON.parse(data.text);
        setHomepageItems(Array.isArray(parsed) ? parsed : []);
      } catch (err) {
        console.error('Error fetching homepage config:', err);
        setHomepageItems([]);
      }
    }

    fetchHomepageConfig();
  }, []);

  useEffect(() => {
    async function fetchBannerConfig() {
      try {
        const { data, error } = await supabase
          .from('texts')
          .select('text')
          .eq('name', 'banner_config')
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        if (!data || !data.text) {
          setBannerConfig(null);
          return;
        }

        const parsed = JSON.parse(data.text);
        setBannerConfig({
          enabled: parsed.enabled ?? false,
          image_url: parsed.image_url ?? '',
          alt_text: parsed.alt_text ?? 'Banner',
          link_type: parsed.link_type ?? 'none',
          link_url: parsed.link_url ?? '',
          link_project_id: parsed.link_project_id ?? null,
          max_width: parsed.max_width ?? 100,
          max_height: parsed.max_height ?? 300,
        });
      } catch (err) {
        console.error('Error fetching banner config:', err);
        setBannerConfig(null);
      }
    }

    fetchBannerConfig();
  }, []);

  const appStyle = isHome
    ? { height: '100vh', overflow: 'hidden' }
    : { minHeight: '100vh', overflowY: 'auto' };

  const openBannerLink = () => {
    if (!bannerConfig) return;
    if (bannerConfig.link_type === 'internal' && bannerConfig.link_project_id) {
      navigate(`/exhibitions/${bannerConfig.link_project_id}`);
      return;
    }
    const url = bannerConfig.link_type === 'external' ? bannerConfig.link_url : bannerConfig.link_url || 'https://www.e-flux.com/announcements/6784278/jakub-jansa-selmeci-kocka-jusko-and-peter-sit-at-the-2026-venice-biennale';
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const closeWelcomePopup = () => {
    setShowWelcomePopup(false);
  };

  return (
    <div className="App" style={appStyle}>
      {showWelcomePopup && bannerConfig && (
        <div className="first-visit-popup-overlay" role="dialog" aria-modal="true" onClick={closeWelcomePopup}>
          <div className="first-visit-popup" onClick={(e) => { e.stopPropagation(); openBannerLink(); }}>
            <img
              className="popup-banner"
              src={bannerConfig.image_url || `${process.env.PUBLIC_URL}/web-banner.svg`}
              alt={bannerConfig.alt_text || 'Homepage banner'}
              style={{
                width: '100%',
                maxWidth: bannerConfig.max_width ? `${bannerConfig.max_width}%` : '100%',
                maxHeight: bannerConfig.max_height ? `${bannerConfig.max_height}px` : '90vh',
              }}
            />
          </div>
        </div>
      )}
      {isHome && <Header onCVClick={() => setCVVisible(!cvVisible)} cvVisible={cvVisible} setCVVisible={setCVVisible} />}
      <Routes>
        <Route path="/" element={
          <>
            <Showreel media={homepageMedia} homepageItems={homepageItems} />
          </>
        } />
        <Route path="/exhibitions/:projectId" element={<Project onCVClick={() => setCVVisible(!cvVisible)} cvVisible={cvVisible} setCVVisible={setCVVisible} />} />
        <Route path="/admin" element={<AdminCMS />} />
        <Route path="/test-layout" element={<TwoColumnLayout />} />
      </Routes>
      {cvVisible && <CV visible={cvVisible} onClose={() => setCVVisible(false)} />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
